import { S } from './state.js';
import { openRtc } from './rtc.js';

// ============================================================
//  Сетевой слой: подключение, измерение пинга, выбор транспорта.
//
//  ВАЖНО про архитектуру: этот модуль — единственное место, которое знает
//  про транспорт. Внутри их ДВА — WebSocket и WebRTC DataChannel, — и оба
//  подняты одновременно; игровой трафик идёт по выбранному игроком, второй
//  только меряет пинг. Остальной клиент об этом не знает: наружу торчат лишь
//  send()/onMessage и поля S.ping / S.pingAlt / S.netState / S.transport.
//
//  Протокол бинарный (не JSON) с первого дня: менять его потом больно,
//  а разбор JSON на 30 снапшотах в секунду — лишняя нагрузка на GC.
//  Раскладка кадров зеркалит server/game/shared/src/proto.rs.
// ============================================================

// ---- Опкоды (должны совпадать с shared/src/proto.rs) ----
// ⚠️ БАГ-УРОК (шаг 5): здесь не хватало FIRE/RELOAD/SWITCH, хотя код их уже
// использовал. `OP.FIRE` возвращал undefined, `setUint8` писал 0, сервер видел
// неизвестный опкод и РВАЛ СОЕДИНЕНИЕ на первом выстреле. Ни ESLint, ни сборка
// такого не ловят (обращение к несуществующему свойству — валидный JS), а живой
// тест был написан на своих константах и повторял ошибку мимо кассы.
// Поэтому таблица завёрнута в Proxy: обращение к неизвестному опкоду теперь
// падает сразу и громко, а не превращается в тихий нолик.
const OPCODES = {
  // клиент → сервер
  PING: 0x01,
  HELLO: 0x02,
  INPUT: 0x03,
  FIRE: 0x04,
  RELOAD: 0x05,
  SWITCH: 0x06,
  THROW: 0x07,
  WHOIS: 0x08,
  PICK: 0x09,
  QUIT: 0x0a,
  // сервер → клиент
  PONG: 0x81,
  WELCOME: 0x82,
  SNAPSHOT: 0x83,
  JOIN: 0x84,
  LEAVE: 0x85,
  SELF: 0x86,
  HIT: 0x87,
  DAMAGE: 0x88,
  KILL: 0x89,
  AMMO: 0x8a,
  VERSION_MISMATCH: 0x8b,
  NADE: 0x8c,
  BOOM: 0x8d,
  FLASH: 0x8e,
  SOUND: 0x8f,
  SHOT: 0x90,
  LOBBY: 0x91,
  MATCH: 0x92,
  SCORES: 0x93,
};

export const OP = new Proxy(OPCODES, {
  get(t, k) {
    if (typeof k === 'string' && !(k in t)) {
      throw new Error(`net.js: обращение к неизвестному опкоду OP.${k}`);
    }
    return t[k];
  },
});

// Биты ввода — зеркало shared/src/movement.rs
//
// Старший байт — «что в руках». ⚠️ На движение эти биты не влияют вообще:
// ни `stepPhysics`, ни серверный `movement::step` их не читают. Они едут
// в маске кнопок просто потому, что это уже существующий канал намерения,
// и три бита здесь дешевле, чем растить кадр OP_INPUT. Сервер перекладывает
// их в байт `held` снапшота, чтобы противник видел, что у нас в руках.
//
// ⚠️ Античит: это ЧИСТО КОСМЕТИКА. Разброс по-прежнему считается от флага ADS
// в кадре ВЫСТРЕЛА (там он зафиксирован на момент самого выстрела), а не отсюда.
export const BTN = {
  W: 1 << 0, S: 1 << 1, A: 1 << 2, D: 1 << 3,
  SPRINT: 1 << 4, JUMP: 1 << 5, CROUCH: 1 << 6, PRONE: 1 << 7,
  ADS: 1 << 8, NADE: 1 << 9,
};
/** Тип гранаты в руке — два бита маски ввода (зеркало movement.rs) */
export const BTN_NADE_SHIFT = 10;

// ---- Байт «что в руках» из снапшота — зеркало shared/src/proto.rs ----
//
// Как и питч, поле ЧИСТО ГРАФИЧЕСКОЕ: на попадания и урон не влияет никак.
// Оружие для расчёта выстрела сервер берёт из своей копии состояния игрока.
export const HELD_ITEM_MASK = 0b111;   // номер оружия (0..2) либо гранаты (0..3)
export const HELD_NADE = 1 << 3;       // в руке граната, а не ствол
export const HELD_ADS = 1 << 4;        // целится (ПКМ)
export const HELD_RELOAD = 1 << 5;     // перезаряжается
export const HELD_DRAW = 1 << 6;       // достаёт предмет (анимация смены)
// ⚠️ Единственный бит этого байта, за которым стоит не только картинка:
// тем же признаком сервер запрещает выстрел (`room.rs::fire`).
export const HELD_SPRINT = 1 << 7;     // бежит: оружие прижато к корпусу

// Флаги игрока в снапшоте — зеркало shared/src/proto.rs
export const F_ALIVE = 1 << 0, F_CROUCH = 1 << 1, F_PRONE = 1 << 2, F_GROUND = 1 << 3;
// F_NPC — это серверный бот, а не живой игрок (shared/src/proto.rs).
// Отличается только отрисовкой: цвет + полоска HP. Всё остальное — позиция,
// поза, курс, урон, PVS — у бота и игрока устроено одинаково.
export const F_NPC = 1 << 4;
// Игрок в команде контр-террористов (иначе — террорист). В тренировке команд
// нет вовсе, и бит всегда погашен: режим клиент узнаёт из кадра OP_MATCH.
// ⚠️ Косметика: цвет тела, ника и метки на радаре. Кому можно нанести урон,
// решает сервер по своей копии команды (`room.rs::friendly`).
export const F_CT = 1 << 6;
// Горит: попал в лужу молотова и догорает ещё FIRE_BURN секунд после того,
// как из неё вышел. Урон считает сервер, здесь по флагу только пламя на теле
// (remote.js) и оранжевая кайма своего экрана (grenades.js).
export const F_BURNING = 1 << 5;

// ---- Квантование (зеркало shared/src/proto.rs) ----
const TAU = Math.PI * 2;
export const packPos = (v) => Math.max(-32768, Math.min(32767, Math.round(v * 100)));
export const unpackPos = (v) => v / 100;
export const packAngle = (a) => { let x = a % TAU; if (x < 0) x += TAU; return (x / TAU) * 65536 | 0; };
export const unpackAngle = (a) => (a / 65536) * TAU;
export const MAX_PITCH = 1.6;
export const packPitch = (a) => Math.max(-MAX_PITCH, Math.min(MAX_PITCH, a)) * 10000 | 0;
export const unpackPitch = (a) => a / 10000;
// Питч в один байт — только в снапшоте, и только чтобы повернуть руки чужого.
// Грубее i16 из OP_INPUT намеренно: там питч задаёт луч выстрела (0.01 рад =
// 40 см мимо на сорока метрах), здесь — угол рук, где 0.7° не видно.
// ⚠️ На хитбоксы не влияет: тело на сервере — вертикальная стопка коробок.
export const unpackPitch8 = (v) => Math.max(-1, Math.min(1, v / 127)) * MAX_PITCH;
export const unpackEye = (v) => v / 1000;
// Высота глаз в снапшоте — один байт, сантиметры (в OP_SELF она миллиметровая:
// там по ней переигрывается предсказание, здесь — задаётся ПОЗА чужого).
// ⚠️ В отличие от pitch8 и held, это НЕ косметика: тем же числом сервер
// раскладывает коробки попаданий.
export const unpackEye8 = (v) => v / 100;
export const unpackUnit = (v) => v / 255;

const PING_INTERVAL = 1000;   // мс между пингами
const RECONNECT_MAX = 8000;   // потолок паузы переподключения

/** Версия протокола — должна совпадать с shared/src/lib.rs */
// ⚠️ ЗЕРКАЛО `shared/src/lib.rs::PROTOCOL_VERSION` — поднимать ТОЛЬКО ПАРОЙ.
//
// Баг-урок: версия поднималась в Rust (v5, наклон Q/E), а эта строка осталась
// на 4. Сервер честно отверг клиента, тот перестал переподключаться — и это
// выглядело как «пропали боты и не показывается FPS», потому что в игру он
// просто не входил, а боты и HUD появляются только после входа. Симптом не
// имел ничего общего с причиной; нашлось по одной строке в консоли браузера.
//
// Коварство именно в этой паре: её рассинхрон ломает НЕ ту фичу, ради которой
// версию поднимали, а вообще всё — и потому ищется не там.
//
// v6: в снапшот добавлен i8 pitch, запись игрока выросла 13 → 14 байт.
// v7: в снапшот добавлен u8 held («что в руках»), запись 14 → 15 байт;
//     в маске кнопок заняты биты 8..11 (ADS и граната в руке).
// v8: кадр OP_KILL вырос 6 → 10 байт — в хвосте направление и сила удара,
//     по ним клиент роняет тело (см. corpse.js).
// v9: новый кадр OP_WHOIS (клиент→сервер, [op][u16 id]) — запрос потерянного
//     ника. Раскладку существующих кадров НЕ трогает; ответ — обычный JOIN.
//     На unreliable-канале WebRTC кадр JOIN может потеряться, и игрок остался
//     бы «#id» (боты — длинными числами). См. баг-урок «имена → цифры».
// v10: кадр OP_INPUT вырос 13 → 17 байт — в хвосте ack_tick и interp_ms,
//     то есть НАША задержка. По ней сервер считает персональное упреждение
//     видимости вместо прежней общей константы 0.25 с. На движение поля
//     не влияют, позицию ими по-прежнему не сказать.
// v11: в снапшот добавлен u8 eye (высота глаз в сантиметрах), запись игрока
//     выросла 15 → 16 байт. Присед и лёжа стали настоящими позами — колено
//     вперёд, тело горизонтально, — и по высоте глаз теперь считаются И
//     раскладка коробок на сервере (`hit::hitboxes`), И рисунок тела здесь.
//     Прежний источник позы (флаги F_CROUCH/F_PRONE) для этого не годится:
//     он везёт намерение, которое переключается мгновенно, а тело едет плавно.
// v12: в байте held занят бит 7 — HELD_SPRINT («бежит, оружие прижато к
//     корпусу»). Байт не вырос, но его смысл изменился: сервер теперь сам
//     гасит бит ADS на бегу, а клиент v11 нарисовал бы бегущего целящимся —
//     то есть показал бы позу, из которой выстрела не бывает.
// v13: боевые боты. Ломает два кадра сразу:
//     • OP_HELLO вырос на байт — после версии протокола едет выбранная
//       СЛОЖНОСТЬ ботов: [op][u16 proto][u8 difficulty][u8 len][nick];
//     • новый кадр сервер→клиент OP_SHOT — чужой выстрел с точкой вылета
//       и точкой попадания. По нему рисуется трассер, искры и дырка от пули:
//       без него огонь ботов был бы слышен, но не виден.
// v14: звук бега. Байты OP_SOUND те же, но появился вид SND_STEP_RUN = 6 —
//     клиент v13 проиграл бы бегущего врага беззвучно.
// v15: перезарядка по стволу. Байты снова те же, но обобщённый SND_RELOAD = 4
//     заменён тремя видами 7|8|9 (пистолет/винтовка/снайперка). Клиент v14
//     не знал бы их и молчал бы на чужую перезарядку — то есть терял бы
//     информацию «он беззащитен и вот на сколько». Номер 4 больше не занят.
// v16: смерть едет в снапшоте. Байты кадра прежние, но изменился СМЫСЛ поля:
//     убитый ещё 400 мс (`room.rs::DEATH_LINGER`) присылается с ПОГАШЕННЫМ
//     F_ALIVE, тогда как раньше мёртвых в снапшоте не бывало вовсе и флаг был
//     взведён у всех подряд. Без этого единственным признаком смерти оставался
//     одиночный кадр OP_KILL: потерялся на WebRTC (ordered:false,
//     maxRetransmits:0) — и клиент считал смерть уходом за укрытие, то есть
//     убитый просто исчезал без тела (жалоба владельца). Клиент v15 мёртвую
//     запись молча прятал бы и трупа не ронял — ровно тот баг, который чиним.
// v17: выносливость и развод бега с перезарядкой. Кадр OP_SELF вырос 21 → 22
//     байта — в хвосте запас сил (u8, 0..255). Он обязан ехать: от стамины
//     зависит скорость, а значит позиция, и клиент, переигрывающий вводы
//     поверх серверного состояния, без неё предсказывал бы бег там, где
//     сервер уже перевёл игрока на шаг. Заодно изменился смысл бита
//     HELD_SPRINT — теперь это ФАКТ бега (выдохшийся идёт шагом с зажатым
//     Shift), и по тому же факту сервер отменяет перезарядку и отвергает
//     попытку начать её на бегу.
// v18: молотов стал бутылкой, а не гранатой. Кадр OP_BOOM вырос 8 → 9 байт —
//     в хвосте ПОВЕРХНОСТЬ удара (grenade::Face, 255 = «нет»). Без неё клиент
//     не нарисует то, что считает сервер: бутылка бьётся о первое касание
//     (стену, потолок, человека), огонь разливается по этой поверхности и по
//     полу под ней. Заодно занят бит 5 флагов игрока — F_BURNING («горит»):
//     попавший в огонь догорает ещё 5 с вне лужи, и клиент v17 показывал бы
//     этот урон как «HP тает само по себе».
// v19: пуля прошивает игроков, попадание сбивает шаг. Ломает две вещи:
//     • кадр OP_SELF вырос 22 → 23 байта — в хвосте остаток окна «в меня
//       сейчас попадают» (u8, доля от movement::HURT_SLOW). Едет по той же
//       причине, что стамина: пока окно не истекло, бег запрещён и скорость
//       умножена на HURT_SPEED_MUL, то есть от него зависит позиция;
//     • изменился СМЫСЛ бита SHOT_BODY в кадре OP_SHOT. Раньше он значил
//       «пуля кончилась в теле» и запрещал ставить дырку; теперь тело пулю
//       не останавливает, конец луча — всегда стена, а бит говорит лишь
//       «по пути были люди» (брызги крови). Клиент v18 именно по нему НЕ
//       поставил бы дырку за пробитыми — то есть потерял бы всю правку.
// v20: МАТЧМЕЙКИНГ — лобби, комнаты, режимы, команды. Ломает три вещи сразу:
//     • новые кадры клиент→сервер OP_PICK (занять слот) и OP_QUIT (вернуться
//       в лобби). ⚠️ Главное: OP_HELLO больше НЕ вводит в игру — он только
//       представляет игрока лобби. Клиент 19-й версии этих кадров не шлёт,
//       то есть на новом сервере он просто не попал бы в игру вовсе;
//     • новые кадры сервер→клиент OP_LOBBY (список комнат), OP_MATCH
//       (режим, фаза, обратный отсчёт, таймер, счёт, своя команда) и
//       OP_SCORES (таблица убийств по Tab);
//     • в флагах игрока занят бит 6 — F_CT («в команде контр-террористов»).
//       Байты снапшота те же, но клиент v19 красил бы союзников как врагов.
export const PROTOCOL_VERSION = 20;

/** Сложность ботов — зеркало server/src/npc.rs::Difficulty */
export const DIFFICULTY = { peaceful: 0, lite: 1, medium: 2, hard: 3 };

// ---- Матчмейкинг: режимы, команды, фазы (зеркало shared/src/proto.rs) ----

/** Тренировка: прежняя песочница — вход в любой момент, реген, без команд */
export const MODE_TRAINING = 0;
/** Дезматч: две команды по TEAM_SLOTS, отсчёт, таймер, счёт, БЕЗ регена HP */
export const MODE_DEATHMATCH = 1;

export const TEAM_T = 0, TEAM_CT = 1, TEAM_NONE = 255;
/** Слотов в команде. Свободные добивают боты на старте матча. */
export const TEAM_SLOTS = 5;
/** Сколько людей нужно, чтобы дезматч начался */
export const MATCH_MIN_HUMANS = 2;

export const PHASE_WAITING = 0, PHASE_COUNTDOWN = 1, PHASE_LIVE = 2, PHASE_OVER = 3;

/** Длина записи в кадре OP_LOBBY / OP_SCORES — зеркало proto.rs */
export const LOBBY_ENTRY_LEN = 10, SCORE_ENTRY_LEN = 6;
/** Флаги записи таблицы очков */
export const SCORE_NPC = 1 << 0, SCORE_ALIVE = 1 << 1;

/** Названия команд для интерфейса */
export const TEAM_NAME = ['Террористы', 'Контр-террористы'];
export const TEAM_SHORT = ['T', 'CT'];

/** Типы гранат в протоколе — зеркало shared/src/grenade.rs::NadeKind */
export const NADE_KIND = { frag: 0, smoke: 1, flash: 2, molotov: 3 };
export const NADE_NAME = ['frag', 'smoke', 'flash', 'molotov'];

// Адрес WebSocket. В проде nginx проксирует /ws на 127.0.0.1:8080,
// в dev (vite на :5173) сервер поднимается отдельно на :8080.
function wsUrl() {
  if (import.meta.env.VITE_WS_URL) return import.meta.env.VITE_WS_URL;
  const host = location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return 'ws://127.0.0.1:8080/ws';
  return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;
}

// ============================================================
//  ДВА ТРАНСПОРТА
//
//  Игровой трафик идёт по одному из них — «основному» (S.transport), который
//  игрок выбирает на стартовом экране. Но подключены ОБА, и оба меряют пинг
//  своим OP_PING. Смысл в двух вещах:
//
//  1. Сравнение честное. Пинги считаются одним и тем же кадром на одном и том
//     же сервере в одну и ту же секунду — а не «вчера по WS, сегодня по RTC».
//  2. Если один транспорт не поднялся (прокси режет WebSocket, анти-DDoS ест
//     UDP), это видно СРАЗУ на стартовом экране, а не по «игра не работает».
//
//  ⚠️ Второй, неосновной линк — только пинг. Он не шлёт hello, значит тела
//  в комнате не занимает и снапшотов не получает (см. баг-урок «над всеми
//  висел ник player»: hello уходит только по-настоящему вошедшего).
// ============================================================

/** Обёртка над WebSocket под общий интерфейс транспорта. */
function openWs(on) {
  let ws;
  try { ws = new WebSocket(wsUrl()); } catch { setTimeout(() => on.close(), 0); return { send() {}, close() {} }; }
  ws.binaryType = 'arraybuffer';
  ws.onopen = () => on.open();
  ws.onmessage = (e) => on.data(e.data);
  ws.onclose = () => on.close();
  // onerror отдельно не обрабатываем: за ошибкой WebSocket всегда следует
  // close, и разводить два пути «связь кончилась» незачем.
  ws.onerror = () => {};
  return {
    send: (b) => { if (ws.readyState === WebSocket.OPEN) ws.send(b); },
    close: () => { try { ws.close(); } catch { /* уже закрыт */ } },
  };
}

const OPENERS = { ws: openWs, rtc: openRtc };
/** Порядок и подписи для UI выбора транспорта. */
export const TRANSPORTS = [
  { kind: 'ws', label: 'WebSocket', hint: 'надёжный, но при потерях пакетов даёт рывки' },
  { kind: 'rtc', label: 'WebRTC', hint: 'UDP-подобный: теряет устаревшее вместо рывка' },
];

function makeLink(kind) {
  return {
    kind,
    sock: null,
    state: 'off',        // 'off' | 'connecting' | 'online' | 'error'
    ping: null,          // сглаженный RTT этого транспорта
    pingSeq: 0,
    pingSent: new Map(), // seq → время отправки (performance.now)
    pingTimer: null,
    reconnectDelay: 500,
    // Поколение соединения. Асинхронная установка WebRTC может завершиться
    // уже после того, как мы это соединение бросили, — без счётчика колбэки
    // мертвеца дёргали бы живой линк.
    gen: 0,
  };
}

const links = { ws: makeLink('ws'), rtc: makeLink('rtc') };

let inputSeq = 0;
let fireSeq = 0;
let throwSeq = 0;
/** Сервер отверг нас по версии — переподключаться бесполезно */
let versionMismatch = false;
/** Последний тик, полученный в снапшоте — уходит обратно в кадре выстрела */
let lastTick = 0;

/** id, который сервер выдал нам — чтобы не рисовать самого себя как чужого */
export const me = { id: 0 };

const listeners = [];
export function onMessage(fn) { listeners.push(fn); }

/** Основной линк — тот, по которому идёт игра. */
const primary = () => links[S.transport];

/** Состояние и пинг конкретного транспорта — для стартового экрана. */
export function linkInfo(kind) {
  const l = links[kind];
  return { state: l.state, ping: l.ping };
}

const STATE_TXT = {
  off: ['офлайн', ''], connecting: ['подключение…', ''],
  online: ['на связи', 'ok'], error: ['недоступен (играем локально)', 'bad'],
};

/**
 * Свести состояние основного линка в S и в HUD.
 *
 * ⚠️ Наружу (в S.netState) едет состояние ТОЛЬКО основного транспорта:
 * по нему весь остальной клиент решает, играем мы в онлайне или локально.
 * Второй линк на это влиять не должен — он всего лишь меряет пинг.
 */
function applyState() {
  const l = primary();
  S.netState = l.state;
  S.ping = l.ping;
  S.pingAlt = links[S.transport === 'ws' ? 'rtc' : 'ws'].ping;
  const el = document.getElementById('netState');
  if (!el || versionMismatch) return;
  const [text, cls] = STATE_TXT[l.state] || STATE_TXT.off;
  el.textContent = text; el.className = cls;
}

function setLinkState(link, v) {
  link.state = v;
  applyState();
}

/** Поднять оба транспорта. Вызывается один раз при открытии страницы. */
export function connect() {
  connectLink(links.ws);
  connectLink(links.rtc);
}

function connectLink(link) {
  if (link.sock || versionMismatch) return;
  const gen = ++link.gen;
  const fresh = (fn) => (...a) => { if (gen === link.gen) fn(...a); };
  setLinkState(link, 'connecting');
  link.sock = OPENERS[link.kind]({
    open: fresh(() => onLinkOpen(link)),
    data: fresh((buf) => onLinkFrame(link, buf)),
    close: fresh(() => onLinkClose(link)),
  });
}

function onLinkOpen(link) {
  link.reconnectDelay = 500;
  setLinkState(link, 'online');
  // Представляемся ТОЛЬКО когда игрок реально вошёл в игру (ввёл ник) И
  // только по основному транспорту.
  // ⚠️ Раньше hello уходил сразу при открытии страницы, с заглушкой 'player' —
  // а сервер намеренно игнорирует повторный hello (иначе сменой ника можно
  // было бы переспавниться), поэтому настоящий ник он не узнавал никогда,
  // и над всеми висела табличка «player». Заодно висящая вкладка не занимает
  // тело в комнате до входа — и по той же причине второй, «пинговый» линк
  // не представляется вовсе. Пинг это не ломает: PING/PONG работает без hello.
  if (S.nick && link === primary()) sendHello();
  clearInterval(link.pingTimer);
  link.pingTimer = setInterval(() => sendPing(link), PING_INTERVAL);
  sendPing(link);
}

function onLinkFrame(link, buf) {
  const d = new DataView(buf);
  const op = d.getUint8(0);
  if (op === OP.PONG) {
    const seq = d.getUint32(1, true);
    const t = link.pingSent.get(seq);
    if (t !== undefined) {
      link.pingSent.delete(seq);
      const rtt = performance.now() - t;
      // Сглаживание (EMA): «сырой» RTT скачет и в HUD выглядит нервно
      link.ping = link.ping === null ? rtt : link.ping * 0.8 + rtt * 0.2;
      applyState();
    }
    return;
  }
  if (op === OP.VERSION_MISMATCH) {
    // Сервер обновился, а вкладка висит со старой сборкой. Переподключение
    // не поможет — нужен перезагруз страницы, о чём и говорим прямо.
    versionMismatch = true;
    const want = d.getUint16(1, true);
    console.error(`Клиент устарел: сервер ждёт протокол ${want}, у нас ${PROTOCOL_VERSION}.`);
    const el = document.getElementById('netState');
    if (el) { el.textContent = 'клиент устарел — обнови страницу (F5)'; el.className = 'bad'; }
    return;
  }
  // Всё, что ниже, — игровые кадры. Приходить они могут только по основному
  // транспорту (второй не представлялся), но проверку ставим явно: молчаливое
  // «этого не может быть» — как раз то, что потом ищется полдня.
  if (link !== primary()) return;

  if (op === OP.WELCOME) {
    const proto = d.getUint16(1, true);
    me.id = d.getUint16(3, true);
    stopWelcomeRetry();                  // свой id получен — добор больше не нужен
    if (proto !== PROTOCOL_VERSION) {
      // Разъехавшийся протокол — худший вид бага: всё «почти работает»,
      // а поля читаются не те. Лучше громко сказать и не гадать.
      console.error(`Версия протокола не совпала: сервер ${proto}, клиент ${PROTOCOL_VERSION}. Обнови страницу.`);
      setLinkState(link, 'error');
    }
    return;
  }
  if (op === OP.SNAPSHOT) lastTick = d.getUint32(1, true);
  for (const fn of listeners) fn(op, d, buf);
}

function onLinkClose(link) {
  clearInterval(link.pingTimer); link.pingTimer = null;
  link.pingSent.clear();
  link.ping = null;
  link.sock = null;
  // Оборвался ОСНОВНОЙ линк — наш id больше не действителен: реконнект = новый
  // игрок (см. 2.0), сервер выдаст новый id новым кадром WELCOME. Обнуляем,
  // чтобы после переподключения sendHello снова добрал WELCOME (иначе при
  // потере нового WELCOME клиент застрял бы со старым, уже удалённым id).
  if (link === primary()) me.id = 0;
  // Переподключаться со старой сборкой бессмысленно — сервер отвергнет снова,
  // а мы получим бесконечный цикл коннектов.
  if (versionMismatch) { setLinkState(link, 'error'); return; }
  setLinkState(link, 'error');
  setTimeout(() => connectLink(link), link.reconnectDelay);
  link.reconnectDelay = Math.min(link.reconnectDelay * 2, RECONNECT_MAX);
}

/**
 * Выбрать транспорт для игры.
 *
 * ⚠️ Только ДО входа в игру. После hello на сервере уже есть наше тело,
 * привязанное к тому соединению: представься мы вторым, в комнате оказались
 * бы два игрока с одним ником, а брошенное тело висело бы под пулями до
 * таймаута молчания (30 с). Переключение на ходу — это, по сути, реконнект,
 * а сессий и переподключения к своему игроку у нас пока нет (см. 2.0).
 */
export function setTransport(kind) {
  if (!(kind in links) || kind === S.transport) return false;
  if (S.nick) return false;
  S.transport = kind;
  applyState();
  return true;
}

function send(buf) {
  const l = primary();
  if (l.sock && l.state === 'online') l.sock.send(buf);
}

/** Основной транспорт готов принимать игровые кадры? */
const ready = () => primary().state === 'online';

function sendPing(link) {
  // Пинги без ответа копились бы вечно при обрыве — чистим старые
  if (link.pingSent.size > 20) link.pingSent.clear();
  const seq = (link.pingSeq = (link.pingSeq + 1) >>> 0);
  const b = new ArrayBuffer(5), d = new DataView(b);
  d.setUint8(0, OP.PING); d.setUint32(1, seq, true);
  link.pingSent.set(seq, performance.now());
  if (link.sock) link.sock.send(b);
}

/**
 * Отправить кадр ввода.
 *
 * ВАЖНО: клиент шлёт только НАМЕРЕНИЕ (какие клавиши зажаты и куда смотрит),
 * а не свою позицию. В этом весь смысл authoritative-сервера: телепорт и
 * спидхак невозможны не потому, что мы их ловим, а потому что сказать
 * «я нахожусь вот здесь» протоколом просто нельзя.
 */
export function sendInput(buttons, yaw, pitch, dt, lean) {
  if (!ready()) return 0;
  const seq = (inputSeq = (inputSeq + 1) >>> 0);
  const b = new ArrayBuffer(17), d = new DataView(b);
  d.setUint8(0, OP.INPUT);
  d.setUint32(1, seq, true);
  // dt в миллисекундах одним байтом: сервер всё равно зажимает его в 1..50 мс
  d.setUint8(5, Math.max(1, Math.min(50, Math.round(dt * 1000))));
  d.setUint16(6, buttons, true);
  d.setUint16(8, packAngle(yaw), true);
  d.setInt16(10, packPitch(pitch), true);
  // Наклон Q/E: шлём НАМЕРЕНИЕ −1…1, упор в стену считает сервер (и клиент
  // тем же кодом для камеры — player.js::leanShift). Позиции глаз в кадре
  // по-прежнему нет: клиент говорит «насколько выглядываю», а не «где я».
  d.setInt8(12, packLean(lean));
  // Наша задержка: последний увиденный тик + буфер интерполяции. Ровно те же
  // два числа, что давно едут в кадре выстрела, — но здесь по ним считается
  // не перемотка, а ПЕРСОНАЛЬНОЕ упреждение видимости: через сколько мы
  // нарисуем то, что сервер отправит сейчас. Раньше упреждение было общей
  // константой 0.25 с — мало для канала 250 мс и лишняя утечка для канала 20.
  //
  // ⚠️ Позицию этим не сказать: сервер считает по ним только «насколько
  // заранее слать», и результат жёстко зажат сверху (hit::LEAD_MAX).
  d.setUint16(13, lastTick & 0xffff, true);
  d.setUint16(15, Math.min(400, Math.max(0, Math.round(S.interpDelay))), true);
  send(b);
  return seq;
}

/** Наклон в один байт — зеркало `proto.rs::pack_lean`. */
export function packLean(v) {
  return Math.round(Math.max(-1, Math.min(1, v)) * 127);
}
export function unpackLean(v) {
  return Math.max(-1, Math.min(1, v / 127));
}

/**
 * Разброс выстрела — точная копия shared/src/weapons.rs::spread_offset.
 *
 * Обе стороны обязаны получить ОДИН И ТОТ ЖЕ луч: иначе трассер уходит в одну
 * точку, а сервер засчитывает попадание в другую, и игра ощущается сломанной.
 * Эталон (сверен с Rust, продублирован тестом spread_matches_reference_values):
 *   seed 1      → (-0.63820, -0.00680)
 *   seed 42     → ( 0.18200, -0.12560)
 *   seed 123456 → ( 0.03560, -0.25000)
 * `>>> 0` после каждого сдвига обязателен: битовые операции в JS дают
 * знаковое 32-битное, а в Rust это u32.
 */
export function spreadOffset(seed) {
  let x = Math.imul(seed, 2654435761) >>> 0;
  if (x === 0) x = 1;
  const next = () => {
    x = (x ^ (x << 13)) >>> 0;
    x = (x ^ (x >>> 17)) >>> 0;
    x = (x ^ (x << 5)) >>> 0;
  };
  next(); const a = (x % 10000) / 10000 - 0.5;
  next(); const b = (x % 10000) / 10000 - 0.5;
  return [a * 2, b * 2];
}

/** Множитель разброса (зеркало weapons.rs::spread_scale) */
export const spreadScale = (ads, movingFast) => (ads ? 0.08 : 1.7) * (movingFast ? 1.8 : 1);

/** Выстрел. Возвращает номер выстрела — по нему считается разброс. */
export function sendFire(yaw, pitch, weapon, ads, interpMs) {
  if (!ready()) return 0;
  const seq = (fireSeq = (fireSeq + 1) >>> 0);
  const b = new ArrayBuffer(15), d = new DataView(b);
  d.setUint8(0, OP.FIRE);
  d.setUint32(1, seq, true);
  d.setUint16(5, packAngle(yaw), true);
  d.setInt16(7, packPitch(pitch), true);
  d.setUint8(9, weapon);
  d.setUint8(10, ads ? 1 : 0);
  // Последний увиденный тик + буфер интерполяции — по ним сервер поймёт,
  // какой момент мира был у нас на экране, и перемотает туда (lag compensation)
  d.setUint16(11, lastTick & 0xffff, true);
  d.setUint16(13, Math.min(400, Math.round(interpMs)), true);
  send(b);
  return seq;
}

/**
 * Бросок гранаты. Как и выстрел — только НАМЕРЕНИЕ: тип, куда смотрим и
 * насколько замахнулись. Где граната появится и куда полетит, решает сервер
 * от своей копии нашей позиции. Поля «моя граната лежит вот тут» в протоколе
 * просто нет — иначе чит «граната под ногами врага» писался бы одной строкой.
 */
export function sendThrow(kind, yaw, pitch, charge) {
  if (!ready()) return 0;
  const seq = (throwSeq = (throwSeq + 1) >>> 0);
  const b = new ArrayBuffer(11), d = new DataView(b);
  d.setUint8(0, OP.THROW);
  d.setUint32(1, seq, true);
  d.setUint8(5, kind);
  d.setUint16(6, packAngle(yaw), true);
  d.setInt16(8, packPitch(pitch), true);
  d.setUint8(10, Math.max(0, Math.min(255, Math.round(charge * 255))));
  send(b);
  return seq;
}

export function sendReload() {
  const b = new ArrayBuffer(1);
  new DataView(b).setUint8(0, OP.RELOAD);
  send(b);
}

/**
 * Запросить у сервера ник по id.
 *
 * Шлём, встретив в снапшоте игрока/бота, для которого у нас нет имени:
 * кадр JOIN приходит один раз и на unreliable-канале WebRTC может потеряться.
 * Сервер отвечает обычным кадром JOIN. Rate-limit — на стороне remote.js
 * (один id спрашиваем не чаще раза в секунду).
 */
export function sendWhois(id) {
  if (!ready()) return;
  const b = new ArrayBuffer(3), d = new DataView(b);
  d.setUint8(0, OP.WHOIS);
  d.setUint16(1, id, true);
  send(b);
}

/**
 * Занять слот: комната, команда и выбранная мирность ботов.
 *
 * Единственный способ попасть из лобби в игру (кадр `OP_HELLO` только
 * представляет нас серверу). Решает при этом СЕРВЕР: он проверяет режим
 * комнаты, фазу матча и занятость слота, и отказ здесь — обычное дело
 * (слот мог занять другой, пока кадр летел). Ответ приходит не «да/нет»,
 * а состоянием: кадр `OP_MATCH` означает, что мы в комнате, новый кадр
 * `OP_LOBBY` — что остались в лобби.
 */
export function sendPick(room, team, difficulty) {
  if (!ready()) return;
  const b = new ArrayBuffer(5), d = new DataView(b);
  d.setUint8(0, OP.PICK);
  d.setUint16(1, room, true);
  d.setUint8(3, team & 0xff);
  d.setUint8(4, difficulty & 0xff);
  send(b);
}

/** Покинуть матч и вернуться в лобби. Связь при этом остаётся. */
export function sendQuit() {
  if (!ready()) return;
  const b = new ArrayBuffer(1);
  new DataView(b).setUint8(0, OP.QUIT);
  send(b);
}

export function sendSwitch(weapon) {
  const b = new ArrayBuffer(2), d = new DataView(b);
  d.setUint8(0, OP.SWITCH); d.setUint8(1, weapon);
  send(b);
}

// ---- Добор WELCOME ----
//
// WELCOME несёт наш id (me.id), а от него зависит seed разброса выстрела
// (weapons.js: `seq ^ (me.id << 16)` — сервер считает от РЕАЛЬНОГО id, и при
// расхождении трассер уходит не туда, где засчитано попадание) и killfeed
// («Вы» вместо ника). На unreliable-канале WebRTC этот единственный кадр может
// потеряться, а повторный он не приходит сам. Лечим тем же приёмом, что
// OP_WHOIS: пока id неизвестен (0), периодически шлём hello заново — сервер
// на повторный hello ПЕРЕСЫЛАЕТ WELCOME, не создавая второго игрока (conn.rs).
// Останавливаемся, как только id пришёл. На WebSocket WELCOME надёжен, и
// повтор просто не понадобится.
let welcomeTimer = null;
const WELCOME_RETRY_MS = 600;

function armWelcomeRetry() {
  if (welcomeTimer) return;               // уже ждём — второй таймер не нужен
  welcomeTimer = setInterval(() => {
    if (me.id !== 0 || versionMismatch || !S.nick) { stopWelcomeRetry(); return; }
    if (ready()) sendHello();
  }, WELCOME_RETRY_MS);
}

function stopWelcomeRetry() {
  clearInterval(welcomeTimer);
  welcomeTimer = null;
}

/**
 * Представиться серверу. Ник сервер всё равно перепроверит и обрежет сам.
 *
 * Без ника не шлём вовсе: заглушка намертво прописалась бы игроку (сервер
 * игнорирует повторный hello), см. комментарий в `ws.onopen`.
 */
export function sendHello() {
  if (!S.nick) return;
  const nick = new TextEncoder().encode(S.nick);
  const b = new ArrayBuffer(5 + nick.length), d = new DataView(b);
  d.setUint8(0, OP.HELLO);
  d.setUint16(1, PROTOCOL_VERSION, true);   // сервер отвергнет несовпадение
  // Мирность ботов. ⚠️ Настройка КОМНАТНАЯ, а не наша личная: комната в
  // проекте одна на всех, и вошедший последним меняет сложность всем.
  // Сервер трактует неизвестное значение как «мирные», поэтому клампить
  // здесь нечего — но и врать нечем: боты от этого байта только злее,
  // а на собственный урон и попадания он не влияет никак.
  d.setUint8(3, S.difficulty | 0);
  d.setUint8(4, nick.length);
  new Uint8Array(b, 5).set(nick);
  send(b);
  // Пока не узнали свой id — не отстаём: WELCOME мог потеряться (см. выше).
  if (me.id === 0) armWelcomeRetry();
}
